import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { TableFilterState, TableSortingState } from '@ibsys/kit';
import {
  buildConversationSelectionKey,
  CONVERSATION_IDS_FILTER_ID,
  CONVERSATION_SELECTION_QUERY_PARAM,
  type ConversationSelection,
  getConversationSelectionKey,
  isConversationSelection,
  normalizeConversationSelectionSource,
  takeConversationSelection,
} from '@/entities/conversations';
import { useTablePresets } from '@/entities/settings';
import { DEFAULT_DIALOG_SORTING } from './constants';

type Params = {
  /** Имя таблицы — ключ в `settings.tables` (например, `'dialogsListV2'`) */
  tableName: string;
  /** Личные фильтры таблицы (рабочая запись) */
  filters: TableFilterState;
  /** Личная сортировка таблицы (рабочая запись) */
  sorting: TableSortingState;
  /** Ключ выборки, для которой подсказка уже отклонена */
  dismissedSelectionKey: string | undefined;
  onDismissedSelectionKeyChange: (key: string | null) => void;
  /** Сохранённое описание активной выборки (привязано к её ключу) */
  selectionDescription: { key: string; text: string; source?: string } | undefined;
  onSelectionDescriptionChange: (value: { key: string; text: string; source: string } | null) => void;
};

/**
 * Выборка без состава диалогов: ею выборка названа и в описании отбора, и в автоимени пресета,
 * а сам состав для этого не нужен.
 */
export type AppliedSelection = Omit<ConversationSelection, 'conversationIds'>;

type Result = {
  /** Фильтры таблицы с уже применённой выборкой перехода — их следует показывать и отправлять на бэк */
  filters: TableFilterState;
  /** Сортировка таблицы с учётом перехода (на свежем переходе — умолчание списка) */
  sorting: TableSortingState;
  /** Ключ действующей выборки среди фильтров; `null` — выборки нет */
  activeSelectionKey: string | null;
  /** Действующая выборка — ею она называется вместо номеров диалогов; `null` — неизвестна */
  activeSelection: AppliedSelection | null;
  /** Выборка, которую стоит предложить сохранить; `null` — подсказка не нужна */
  unsavedSelection: AppliedSelection | null;
  /** Пользователь скрыл подсказку, не сохраняя выборку */
  onDismiss: () => void;
};

/**
 * Принимает внешнюю выборку диалогов и открывает её отдельным слоем
 * вида поверх личных настроек таблицы: применяет только саму выборку и сортировку по умолчанию
 * (по идентификатору диалога по убыванию), оставляя колонки пользователя; личные фильтры и
 * сортировка не применяются, а откладываются в `previousView` для возврата при снятии слоя.
 *
 * Выборка живёт среди фильтров таблицы (фильтр по идентификаторам диалогов) — так она попадает
 * в снимок пресета при сохранении. Пользователю поле фильтра не предлагается.
 *
 * Каналов доставки два: состояние навигации (переход в текущей вкладке) и хранилище браузера
 * по ключу в адресе (переход в новую вкладку). Дальше приёма канал ни на что не влияет —
 * распознанная выборка идёт общим путём.
 *
 * Переход одноразовый: принятая выборка снимается с канала доставки. Иначе она возвращалась бы
 * в отбор уже после того, как пользователь её снял, — состояние навигации браузер восстанавливает
 * и при перезагрузке, и при любом возврате к адресу, а выборка из хранилища живёт в памяти
 * страницы до конца её жизни и легла бы заново при первой же навигации внутри раздела.
 *
 * «Сохранена ли выборка» выводится не из нажатия кнопки в этом сеансе жизни страницы, а из самих
 * пресетов пользователя: выборка сохранена, если есть пресет с тем же составом диалогов. Отклонение
 * подсказки хранится рядом с настройками таблицы и привязано к составу выборки — переход
 * по другому инсайту приносит другую выборку и показывает подсказку заново.
 *
 * Описание выборки приходит только вместе с самим переходом, но уход в карточку диалога заменяет
 * состояние навигации (лейаут «таблица + карточка» выбирает диалог сразу), и подсказка пропадала бы.
 * Поэтому при применении выборки описание дублируется в настройки таблицы и берётся оттуда,
 * пока состав выборки в фильтрах не сменился.
 */
export const useExternalSelection = ({
  tableName,
  filters,
  sorting,
  dismissedSelectionKey,
  onDismissedSelectionKeyChange,
  selectionDescription,
  onSelectionDescriptionChange,
}: Params): Result => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { presets, enterView } = useTablePresets(tableName);

  // Выборка, открытая новой вкладкой: в адресе лежит только ключ к ней. Забираем её при
  // открытии страницы — хранилище отдаёт выборку и сразу снимает, чтобы повторный заход по
  // тому же адресу не открыл устаревший состав.
  const [stashedSelection, setStashedSelection] = useState<ConversationSelection | null>(() => {
    const stashKey = searchParams.get(CONVERSATION_SELECTION_QUERY_PARAM);
    return stashKey ? takeConversationSelection(stashKey) : null;
  });

  // Выборка перехода в текущей вкладке приходит состоянием навигации
  const navigationSelection = isConversationSelection(location.state) ? location.state : null;
  const selection = navigationSelection ?? stashedSelection;

  const selectionFilter = useMemo<TableFilterState[number] | null>(
    () => (selection ? { id: CONVERSATION_IDS_FILTER_ID, value: selection.conversationIds } : null),
    [selection],
  );

  const incomingKey = selection ? buildConversationSelectionKey(selection.conversationIds) : null;
  const persistedSelectionKey = getConversationSelectionKey(filters);

  // Свежий переход: выборка пришла каналом перехода и ещё не лежит в отборе таблицы. Совпадение
  // ключей означает, что этот переход уже отработан (например, страницу перезагрузили с применённой
  // выборкой) — входить в слой повторно нельзя, затёрся бы отложенный личный вид.
  const isFreshTransition = selection !== null && persistedSelectionKey !== incomingKey;

  // Выборку и сортировку по умолчанию показываем сразу в рендере, а не в эффекте после него: список
  // данных строится по фильтрам/сортировке уже на первом рендере, и запоздалая их смена приводила к
  // отмене начального запроса без повторного — список залипал в загрузке до перезагрузки страницы.
  const effectiveFilters = useMemo<TableFilterState>(() => {
    if (!isFreshTransition || !selectionFilter) return filters;
    return [selectionFilter];
  }, [isFreshTransition, selectionFilter, filters]);

  const effectiveSorting = useMemo<TableSortingState>(
    () => (isFreshTransition ? DEFAULT_DIALOG_SORTING : sorting),
    [isFreshTransition, sorting],
  );

  // Описание применяемой выборки — для дублирования в настройки таблицы вместе с фильтрами
  const pendingDescriptionRef = useRef<{ key: string; text: string; source: string } | null>(null);
  pendingDescriptionRef.current = selection
    ? {
        key: buildConversationSelectionKey(selection.conversationIds),
        text: selection.description,
        source: selection.source,
      }
    : null;

  // Выборка, уже снятая с адреса: повторное снятие того же состояния — лишний переход
  const clearedNavigationSelectionRef = useRef<ConversationSelection | null>(null);

  // Вход в слой выборки: откладываем личный вид, ставим только выборку и сортировку по умолчанию,
  // колонки пользователя не трогаем. Там же дублируем описание — оно переживёт уход в карточку
  // диалога и перезагрузку страницы.
  //
  // Дальше выборка снимается с канала доставки — переход одноразовый. Снимаем и уже отработанный
  // переход (выборка в отборе, входить в слой не нужно): иначе он остался бы в истории и лёг бы
  // в отбор снова после того, как пользователь выборку снял.
  useEffect(() => {
    if (!selection) return;
    if (isFreshTransition && selectionFilter) {
      enterView({ filters: [selectionFilter], sorting: DEFAULT_DIALOG_SORTING }, { backup: true });
      if (pendingDescriptionRef.current) onSelectionDescriptionChange(pendingDescriptionRef.current);
    }
    setStashedSelection(null);
    if (navigationSelection && clearedNavigationSelectionRef.current !== navigationSelection) {
      clearedNavigationSelectionRef.current = navigationSelection;
      // Адрес остаётся прежним — меняется только состояние текущей записи истории, поэтому
      // сюда идёт не типизированный переход по маршруту, а замена текущего адреса им же:
      // раздел (диалоги или закреплённый пресет) и открытая запись сохраняются как есть.
      navigate(
        { pathname: location.pathname, search: location.search, hash: location.hash },
        { replace: true, state: null },
      );
    }
  }, [
    selection,
    isFreshTransition,
    selectionFilter,
    navigationSelection,
    enterView,
    onSelectionDescriptionChange,
    navigate,
    location.pathname,
    location.search,
    location.hash,
  ]);

  // Действующая выборка — та, что лежит в фильтрах таблицы; снятая фильтром больше не существует
  const activeSelectionKey = useMemo(() => getConversationSelectionKey(effectiveFilters), [effectiveFilters]);

  // Выборку (описание + источник) берём с самого перехода с дашборда либо из настроек
  // таблицы (после ухода в карточку диалога/перезагрузки). Принимаем только
  // описание действующей выборки — иначе фильтр диалогов уже сменился и описание к нему не относится.
  const selectionInfo = useMemo<AppliedSelection | null>(() => {
    if (!activeSelectionKey) return null;
    if (selection && buildConversationSelectionKey(selection.conversationIds) === activeSelectionKey) {
      return { description: selection.description, source: selection.source };
    }
    if (selectionDescription && selectionDescription.key === activeSelectionKey) {
      return {
        description: selectionDescription.text,
        source: normalizeConversationSelectionSource(selectionDescription.source),
      };
    }
    return null;
  }, [selection, activeSelectionKey, selectionDescription]);

  const isSaved = useMemo(
    () => presets.some((preset) => getConversationSelectionKey(preset.settings.filters ?? []) === activeSelectionKey),
    [presets, activeSelectionKey],
  );

  const isDismissed = dismissedSelectionKey === activeSelectionKey;

  const onDismiss = useCallback(() => {
    if (!activeSelectionKey) return;
    onDismissedSelectionKeyChange(activeSelectionKey);
  }, [activeSelectionKey, onDismissedSelectionKeyChange]);

  const isHintNeeded = activeSelectionKey !== null && !isSaved && !isDismissed;

  return {
    filters: effectiveFilters,
    sorting: effectiveSorting,
    activeSelectionKey,
    activeSelection: selectionInfo,
    unsavedSelection: isHintNeeded ? selectionInfo : null,
    onDismiss,
  };
};
